import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { extractError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ROLE_LABELS } from "@/lib/dict";
import { Search, Check, Loader2, ChevronDown, Briefcase, Building2, X, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

const UNLIMITED_ROLES = new Set(["super_admin", "org_admin"]);
const EXTERNAL = new Set(["tenant", "contractor"]);

export default function AccessMatrix() {
  const qc = useQueryClient();
  const [userQuery, setUserQuery] = useState("");
  const [assetQuery, setAssetQuery] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [legalEntityFilter, setLegalEntityFilter] = useState("all");
  const [saving, setSaving] = useState({});

  const { data: users = [] } = useQuery({ queryKey: ["users-all"], queryFn: () => api.get("/users").then((r) => r.data) });
  const { data: assets = [] } = useQuery({ queryKey: ["assets-all"], queryFn: () => api.get("/assets").then((r) => r.data) });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.get("/clients").then((r) => r.data) });
  const { data: entities = [] } = useQuery({ queryKey: ["legal-entities"], queryFn: () => api.get("/legal-entities").then((r) => r.data) });

  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const entityById = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e])), [entities]);

  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/users/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users-all"] }),
    onError: (e) => toast.error(extractError(e, "Échec")),
  });

  const eligibleUsers = useMemo(() =>
    users.filter((u) => !UNLIMITED_ROLES.has(u.role) && !EXTERNAL.has(u.role)),
  [users]);

  const filteredUsers = useMemo(() =>
    eligibleUsers.filter((u) =>
      !userQuery || `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(userQuery.toLowerCase())
    ), [eligibleUsers, userQuery]);

  // Filtered assets by client / legal-entity / search
  const filteredAssets = useMemo(() => {
    return assets.filter((a) => {
      if (clientFilter !== "all" && a.client_id !== clientFilter) return false;
      if (legalEntityFilter !== "all" && a.legal_entity_id !== legalEntityFilter) return false;
      if (assetQuery && !`${a.name} ${a.internal_ref || ""} ${a.city || ""}`.toLowerCase().includes(assetQuery.toLowerCase())) return false;
      return true;
    });
  }, [assets, clientFilter, legalEntityFilter, assetQuery]);

  // Group filtered assets by (client → legal entity) for header rendering
  const grouped = useMemo(() => {
    const map = new Map(); // clientId -> { client, entities: Map<leId, {le, assets:[]}> }
    for (const a of filteredAssets) {
      const cid = a.client_id || "__none__";
      const lid = a.legal_entity_id || "__none__";
      if (!map.has(cid)) map.set(cid, { client: clientById[cid], entities: new Map() });
      const clientBucket = map.get(cid);
      if (!clientBucket.entities.has(lid)) clientBucket.entities.set(lid, { le: entityById[lid], assets: [] });
      clientBucket.entities.get(lid).assets.push(a);
    }
    // Flatten to array of { client, entities: [{le, assets}] }, preserving order
    return Array.from(map.values()).map((cb) => ({
      client: cb.client,
      clientId: cb.client?.id || "__none__",
      entities: Array.from(cb.entities.values()),
    }));
  }, [filteredAssets, clientById, entityById]);

  const orderedAssetIds = useMemo(() => grouped.flatMap((g) => g.entities.flatMap((e) => e.assets.map((a) => a.id))), [grouped]);
  const legalEntitiesForClient = useMemo(() =>
    clientFilter === "all" ? entities : entities.filter((e) => e.client_id === clientFilter),
  [entities, clientFilter]);

  const setAssetsForUser = async (u, newIds, globalAccess = false) => {
    const key = `bulk:${u.id}`;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await update.mutateAsync({ id: u.id, patch: { assigned_asset_ids: Array.from(newIds), has_global_access: globalAccess } });
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  const toggle = async (u, assetId) => {
    const current = new Set(u.has_global_access ? [] : (u.assigned_asset_ids || []));
    if (current.has(assetId)) current.delete(assetId); else current.add(assetId);
    const key = `${u.id}:${assetId}`;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await update.mutateAsync({ id: u.id, patch: { assigned_asset_ids: Array.from(current), has_global_access: false } });
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  const bulkGrantClient = (u, clientId, grant) => {
    const targetIds = new Set(assets.filter((a) => a.client_id === clientId).map((a) => a.id));
    const current = new Set(u.has_global_access ? [] : (u.assigned_asset_ids || []));
    // If user had global access, grant only asset ids of this client (so it becomes restricted).
    if (u.has_global_access && grant) {
      return setAssetsForUser(u, targetIds, false);
    }
    for (const id of targetIds) grant ? current.add(id) : current.delete(id);
    return setAssetsForUser(u, current, false);
  };

  const bulkGrantLegalEntity = (u, leId, grant) => {
    const targetIds = new Set(assets.filter((a) => a.legal_entity_id === leId).map((a) => a.id));
    const current = new Set(u.has_global_access ? [] : (u.assigned_asset_ids || []));
    if (u.has_global_access && grant) return setAssetsForUser(u, targetIds, false);
    for (const id of targetIds) grant ? current.add(id) : current.delete(id);
    return setAssetsForUser(u, current, false);
  };

  const bulkGrantVisible = (u, grant) => {
    const targetIds = new Set(orderedAssetIds);
    const current = new Set(u.has_global_access ? [] : (u.assigned_asset_ids || []));
    if (u.has_global_access && grant) return setAssetsForUser(u, targetIds, false);
    for (const id of targetIds) grant ? current.add(id) : current.delete(id);
    return setAssetsForUser(u, current, false);
  };

  const clearAll = (u) => setAssetsForUser(u, new Set(), true);

  // Header column groups
  const totalCols = orderedAssetIds.length;

  return (
    <Card className="border-slate-200 overflow-hidden">
      {/* Header + Filters */}
      <div className="px-4 py-3 border-b border-slate-200 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display font-semibold">Matrice d&apos;accès utilisateurs × actifs</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {filteredAssets.length} / {assets.length} actif(s) affiché(s) • {filteredUsers.length} utilisateur(s). Aucun site sélectionné = aucun accès, sauf si &quot;accès global&quot; est activé.
            </div>
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="matrix-search-user" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder="Rechercher un utilisateur..." className="pl-8 h-8 w-64 text-sm" />
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={clientFilter} onValueChange={(v) => { setClientFilter(v); setLegalEntityFilter("all"); }}>
            <SelectTrigger data-testid="filter-client" className="w-56 h-8 text-sm bg-white"><SelectValue placeholder="Filtrer par client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les clients ({clients.length})</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={legalEntityFilter} onValueChange={setLegalEntityFilter} disabled={legalEntitiesForClient.length === 0}>
            <SelectTrigger data-testid="filter-legal-entity" className="w-56 h-8 text-sm bg-white"><SelectValue placeholder="Filtrer par entité juridique" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les entités ({legalEntitiesForClient.length})</SelectItem>
              {legalEntitiesForClient.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="filter-asset" value={assetQuery} onChange={(e) => setAssetQuery(e.target.value)} placeholder="Rechercher un actif..." className="pl-8 h-8 w-52 text-sm" />
          </div>
          {(clientFilter !== "all" || legalEntityFilter !== "all" || assetQuery) && (
            <Button variant="ghost" size="sm" className="h-8" onClick={() => { setClientFilter("all"); setLegalEntityFilter("all"); setAssetQuery(""); }}>
              <X size={13} className="mr-1" /> Réinitialiser filtres
            </Button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {filteredAssets.length === 0 && (
        <div className="p-10 text-center text-sm text-slate-400">Aucun actif ne correspond aux filtres appliqués.</div>
      )}

      {filteredAssets.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              {/* Row 1: Client groups */}
              <tr>
                <th rowSpan={3} className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 sticky left-0 bg-slate-50 z-20 min-w-[240px]">Utilisateur</th>
                <th rowSpan={3} className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 sticky left-[240px] bg-slate-50 z-20 min-w-[100px]">Rôle</th>
                <th rowSpan={3} className="text-center px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 min-w-[100px]">Actions</th>
                {grouped.map((g) => {
                  const colSpan = g.entities.reduce((s, e) => s + e.assets.length, 0);
                  return (
                    <th key={g.clientId} colSpan={colSpan} className="text-center px-2 py-1.5 border-l border-slate-200 bg-[#023E7308]">
                      <div className="flex items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: "#023E73" }}>
                        <Briefcase size={10} /> {g.client?.name || "— Sans client —"}
                      </div>
                    </th>
                  );
                })}
              </tr>
              {/* Row 2: Legal entity groups */}
              <tr>
                {grouped.flatMap((g) => g.entities.map((e) => (
                  <th key={`${g.clientId}-${e.le?.id || 'none'}`} colSpan={e.assets.length} className="text-center px-2 py-1 border-l border-slate-200 bg-[#023E7304]">
                    <div className="flex items-center justify-center gap-1 text-[10px] font-semibold text-slate-600">
                      <Building2 size={9} /> {e.le?.name || "— Sans entité —"}
                    </div>
                  </th>
                )))}
              </tr>
              {/* Row 3: Asset columns */}
              <tr>
                {grouped.flatMap((g) => g.entities.flatMap((e) => e.assets.map((a, idx) => (
                  <th key={a.id} className={`text-center px-2 py-2 text-[10px] font-semibold text-slate-700 min-w-[90px] max-w-[110px] ${idx === 0 ? "border-l border-slate-200" : ""}`} title={`${a.name}${a.internal_ref ? ` (${a.internal_ref})` : ""} · ${a.city || ""}`}>
                    <div className="truncate">{a.name}</div>
                    {a.internal_ref && <div className="text-[9px] font-mono text-slate-500 mt-0.5">{a.internal_ref}</div>}
                  </th>
                ))))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const assigned = new Set(u.assigned_asset_ids || []);
                const unlimited = u.has_global_access === true;
                const bulkSaving = saving[`bulk:${u.id}`];
                return (
                  <tr key={u.id} data-testid={`matrix-row-${u.id}`} className="border-b border-slate-100 hover:bg-slate-50/40">
                    <td className="px-4 py-2 sticky left-0 bg-white z-10 border-r border-slate-100">
                      <div className="font-semibold text-sm">{u.first_name} {u.last_name}</div>
                      <div className="text-[11px] text-slate-500">{u.email}</div>
                      <div className="text-[10px] font-semibold mt-0.5" style={{ color: unlimited ? "#059669" : assigned.size === 0 ? "#DC2626" : "#023E73" }}>
                        {unlimited ? `Accès complet (${assets.length})` : assigned.size === 0 ? "Aucun accès" : `${assigned.size} actif(s)`}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600 sticky left-[240px] bg-white z-10 border-r border-slate-100">{ROLE_LABELS[u.role]}</td>
                    <td className="px-3 py-2 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button data-testid={`bulk-btn-${u.id}`} variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={bulkSaving}>
                            {bulkSaving ? <Loader2 size={11} className="animate-spin" /> : <>Groupé <ChevronDown size={11} className="ml-0.5" /></>}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-72">
                          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Attribution rapide</DropdownMenuLabel>
                          <DropdownMenuItem data-testid={`bulk-visible-grant-${u.id}`} onClick={() => bulkGrantVisible(u, true)}>
                            <CheckSquare size={13} className="mr-2 text-emerald-600" /> Accorder tous les actifs visibles ({orderedAssetIds.length})
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`bulk-visible-revoke-${u.id}`} onClick={() => bulkGrantVisible(u, false)}>
                            <Square size={13} className="mr-2 text-red-500" /> Retirer tous les actifs visibles
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Par client</DropdownMenuLabel>
                          {clients.map((c) => {
                            const cAssetIds = assets.filter((a) => a.client_id === c.id).map((a) => a.id);
                            if (cAssetIds.length === 0) return null;
                            const cAssigned = cAssetIds.filter((id) => assigned.has(id) || unlimited).length;
                            return (
                              <div key={c.id} className="px-2 py-1 text-xs flex items-center justify-between gap-2 hover:bg-slate-50">
                                <div className="truncate flex-1">
                                  <Briefcase size={10} className="inline mr-1 text-[#023E73]" />
                                  <span className="font-medium">{c.name}</span>
                                  <span className="text-[10px] text-slate-500 ml-1">({cAssigned}/{cAssetIds.length})</span>
                                </div>
                                <div className="flex gap-0.5">
                                  <Button data-testid={`bulk-client-grant-${u.id}-${c.id}`} variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => bulkGrantClient(u, c.id, true)}>✓</Button>
                                  <Button data-testid={`bulk-client-revoke-${u.id}-${c.id}`} variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-red-500" onClick={() => bulkGrantClient(u, c.id, false)}>✗</Button>
                                </div>
                              </div>
                            );
                          })}
                          {clientFilter !== "all" && legalEntitiesForClient.length > 0 && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Par entité juridique</DropdownMenuLabel>
                              {legalEntitiesForClient.map((le) => {
                                const leAssetIds = assets.filter((a) => a.legal_entity_id === le.id).map((a) => a.id);
                                if (leAssetIds.length === 0) return null;
                                const leAssigned = leAssetIds.filter((id) => assigned.has(id) || unlimited).length;
                                return (
                                  <div key={le.id} className="px-2 py-1 text-xs flex items-center justify-between gap-2 hover:bg-slate-50">
                                    <div className="truncate flex-1">
                                      <Building2 size={10} className="inline mr-1 text-slate-500" />
                                      <span>{le.name}</span>
                                      <span className="text-[10px] text-slate-500 ml-1">({leAssigned}/{leAssetIds.length})</span>
                                    </div>
                                    <div className="flex gap-0.5">
                                      <Button data-testid={`bulk-le-grant-${u.id}-${le.id}`} variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => bulkGrantLegalEntity(u, le.id, true)}>✓</Button>
                                      <Button data-testid={`bulk-le-revoke-${u.id}-${le.id}`} variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-red-500" onClick={() => bulkGrantLegalEntity(u, le.id, false)}>✗</Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`bulk-clear-${u.id}`} onClick={() => clearAll(u)}>
                            <X size={13} className="mr-2" /> Vider (accès complet)
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    {grouped.flatMap((g) => g.entities.flatMap((e, ei) => e.assets.map((a, ai) => {
                      const active = unlimited || assigned.has(a.id);
                      const cellKey = `${u.id}:${a.id}`;
                      const firstOfGroup = ei === 0 && ai === 0;
                      return (
                        <td key={a.id} className={`text-center px-2 py-2 ${firstOfGroup ? "border-l border-slate-200" : ""}`}>
                          <button
                            data-testid={`cell-${u.id}-${a.id}`}
                            onClick={() => toggle(u, a.id)}
                            disabled={!!saving[cellKey] || bulkSaving}
                            title={unlimited ? "Accès complet — cliquez pour restreindre à cet actif" : (assigned.has(a.id) ? "Retirer l'accès" : "Ajouter l'accès")}
                            className={`w-7 h-7 rounded-md inline-flex items-center justify-center transition-all ${
                              saving[cellKey] ? "bg-slate-100" :
                              unlimited ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-600 border border-emerald-200 border-dashed" :
                              assigned.has(a.id) ? "text-white shadow-sm" : "bg-slate-100 hover:bg-slate-200 text-slate-400"
                            }`}
                            style={!unlimited && assigned.has(a.id) ? { background: "#023E73" } : {}}
                          >
                            {saving[cellKey] ? <Loader2 size={12} className="animate-spin" /> : active ? <Check size={13} strokeWidth={3} /> : null}
                          </button>
                        </td>
                      );
                    })))}
                  </tr>
                );
              })}
              {filteredUsers.length === 0 && (
                <tr><td colSpan={totalCols + 3} className="text-center py-8 text-sm text-slate-400">Aucun utilisateur ne correspond à la recherche</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500 flex items-center gap-4 flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-emerald-50 border border-dashed border-emerald-300 inline-flex items-center justify-center"><Check size={9} className="text-emerald-600" strokeWidth={3} /></span>
          Accès complet (aucune restriction)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: "#023E73" }} />
          Accès autorisé (restriction active)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-slate-200" />
          Accès refusé
        </span>
      </div>
    </Card>
  );
}
